import { useState, useEffect } from 'react';
import { Copy, Check, ThumbsUp, ThumbsDown, RotateCcw, Languages, Volume2, Loader2 } from 'lucide-react';
import { useCopy } from 'hooks/useCopy';
import { useSetMessageFeedbackMutation } from 'redux/services/chatHistory';
import styles from './message-actions.module.css';

// Action row under an AI message: copy the answer (raw markdown) and 👍/👎
// feedback. Feedback is saved server-side against the message id and re-hydrated
// from history via `initialFeedback`. B2C passes consumerId/clientOrgId (its
// endpoints authorize by query param); B2B relies on the session cookie.
// `hasEnglish` renders a language toggle — only bilingual (non-English) replies
// carry an English variant, so English-only messages never show the button.
// `speechText` is the agent's spoken variant of this answer; when present a
// speaker button offers to synthesize and play it on demand.
const MessageActions = ({
  content,
  messageId,
  sessionId,
  isB2C = false,
  consumerId,
  clientOrgId,
  initialFeedback = null,
  onRegenerate,
  hasEnglish = false,
  englishShown = false,
  onToggleLanguage,
  speechText = null,
  onPlaySpeech,
  isSpeechLoading = false,
  isSpeechActive = false,
}) => {
  const { copied, copy } = useCopy();
  const [feedback, setFeedback] = useState(initialFeedback);
  const [setMessageFeedback] = useSetMessageFeedbackMutation();

  // Sync when saved feedback arrives (e.g. after a history load populates it).
  useEffect(() => {
    setFeedback(initialFeedback ?? null);
  }, [initialFeedback]);

  const canRate = Boolean(messageId && sessionId);

  const applyFeedback = async (next) => {
    if (!canRate) return;
    const value = feedback === next ? null : next; // click the active one again to clear
    const previous = feedback;
    setFeedback(value); // optimistic
    try {
      await setMessageFeedback({
        chatId: sessionId,
        messageId,
        rating: value,
        isB2C,
        consumerId,
        clientOrgId,
      }).unwrap();
    } catch {
      setFeedback(previous); // revert on failure
    }
  };

  return (
    <div className={styles.messageActions}>
      <button
        type="button"
        className={`${styles.actionButton} ${copied ? styles.actionButtonCopied : ''}`}
        onClick={() => copy(content)}
        aria-label={copied ? 'Copied' : 'Copy message'}
        title={copied ? 'Copied' : 'Copy'}
      >
        {copied ? <Check size={15} /> : <Copy size={15} />}
      </button>

      {canRate && (
        <>
          <button
            type="button"
            className={`${styles.actionButton} ${feedback === 'up' ? styles.actionButtonActive : ''}`}
            onClick={() => applyFeedback('up')}
            aria-label="Good response"
            aria-pressed={feedback === 'up'}
            title="Good response"
          >
            <ThumbsUp size={15} />
          </button>
          <button
            type="button"
            className={`${styles.actionButton} ${feedback === 'down' ? styles.actionButtonActive : ''}`}
            onClick={() => applyFeedback('down')}
            aria-label="Bad response"
            aria-pressed={feedback === 'down'}
            title="Bad response"
          >
            <ThumbsDown size={15} />
          </button>
        </>
      )}

      {onRegenerate && (
        <button
          type="button"
          className={styles.actionButton}
          onClick={onRegenerate}
          aria-label="Regenerate response"
          title="Regenerate"
        >
          <RotateCcw size={15} />
        </button>
      )}

      {speechText && onPlaySpeech && (
        <button
          type="button"
          className={`${styles.actionButton} ${isSpeechActive ? styles.actionButtonActive : ''}`}
          onClick={() => onPlaySpeech()}
          disabled={isSpeechLoading}
          aria-label={isSpeechLoading ? 'Generating audio' : 'Read aloud'}
          title={isSpeechLoading ? 'Generating audio…' : 'Read aloud'}
        >
          {isSpeechLoading ? (
            <Loader2 size={15} className={styles.actionButtonSpinner} />
          ) : (
            <Volume2 size={15} />
          )}
        </button>
      )}

      {hasEnglish && onToggleLanguage && (
        <button
          type="button"
          className={`${styles.actionButton} ${englishShown ? styles.actionButtonActive : ''}`}
          onClick={onToggleLanguage}
          aria-label={englishShown ? 'Show original language' : 'Show English'}
          aria-pressed={englishShown}
          title={englishShown ? 'Show original language' : 'Show English'}
        >
          <Languages size={15} />
        </button>
      )}
    </div>
  );
};

export default MessageActions;
